/**
 * Who a text or a call is from: the card stores' `currentProvider`, which the
 * compose modals show on their "From" line and send as a call's `from`.
 *
 * While a staff member charts a patient, pacmny-be puts the PATIENT in
 * `allData.patient` and the staff member in `allData.manager`
 * (DataManagerImpl::getAllData), and says so with `viewingPatientChart`. The
 * stores mapped `allData.patient`, so the modals offered the patient's phone
 * as the caller's own. `viewingPatientChart` is read rather than the presence
 * of `manager`: a cached session can still carry a manager from an earlier
 * visit.
 *
 * The port's blob (`login/getAllData`) keeps that shape — `manager` is the
 * staff member at the keyboard, never the organization, and viewing a chart
 * never changes who is acting (decision D4). One difference: with no chart
 * open a member of staff has no `patient` at all (the port does not put the
 * signed-in person where a chart belongs, because 135 call sites build
 * `/patients/{id}` URLs from it), so the signed-in person comes from
 * `allData.user` instead.
 *
 * Once the session has loaded, the id, name, phone and e-mail are the
 * signed-in person's own (`GET /me`, through sessionActorFrom; main's MySQL
 * self); before that, the manager-vs-patient reading above.
 *
 * keyon chooses the number a call is actually bridged to from its own record
 * of the caller (keyon/lib/TwilioAccess.php); this is the number the caller
 * sees and asks for. `specialty` stays what the stores always had.
 *
 * `id` is the signed-in person's user id whether or not a chart is open (D4:
 * viewing a chart never changes who is acting). AppointmentModule seeds a new
 * visit's provider from it, matched against provider options keyed by users.id
 * (Provider.GetAllAccountProviders `subaccountnumber`). Main kept the chart
 * patient's id here while charting, which on main was an account_user id that
 * matched no provider. On the port the open chart's id is a patients.id, a
 * separate number space, so it pre-selected whichever unrelated staff member's
 * users.id happened to equal it. Before the session loads, the stores' old
 * reading stands (the patient's externid, else nothing).
 *
 * @param {object} allData Vuex login/getAllData
 * @param {object|null|undefined} sessionSelf optional; undefined peeks Pinia
 * @returns {{id: string, name: string, email: string, phone: string, specialty: string}}
 */
import { sessionActorFrom } from '@/utils/sessionIdentity';
import { peekSessionSelfProfile } from '@/stores/sessionSelf';

export function messagingSender(allData, sessionSelf) {
  const patient = allData && allData.patient;
  const user = allData && allData.user;
  if (!patient && !hasName(user)) {
    throw new Error('No provider data available');
  }
  // The signed-in person when no chart is open: the patient on their own
  // chart (main's blob, and the port's for a patient), else the port's `user`.
  const self = hasName(patient) || !hasName(user) ? patient || {} : user;
  const sender = {
    id: (patient && patient.externid) || '',
    name: nameOf(self) || 'Unknown',
    email: self.email || '',
    phone: self.phone1 || self.phone2 || self.phone || self.phone_secondary || '',
    specialty: (patient && patient.specialty) || 'N/A',
  };
  const manager = allData.manager;
  if (allData.viewingPatientChart && manager) {
    sender.name = manager.name || sender.name;
    sender.phone = manager.phone1 || manager.phone2 || '';
    // pacmny-be's manager block carries no email, so there this is usually
    // empty and the modals show the name alone rather than the patient's
    // address beside it; the port's carries the staff member's own. keyon
    // ignores the posted fromEmail.
    sender.email = manager.email || '';
  }

  const actor = sessionActorFrom(allData, peekSessionSelfProfile(sessionSelf));
  if (actor.source === 'session') {
    sender.name = actor.displayName || sender.name;
    sender.phone = actor.phone1 || '';
    sender.email = actor.email || '';
    sender.id = actor.unique_ID || sender.id;
  }
  return sender;
}

function nameOf(person) {
  if (!person) return '';
  const first = person.firstName || person.first_name || person.fname || '';
  const last = person.lastName || person.last_name || person.lname || '';
  return [first, last].filter(Boolean).join(' ') || person.full_name || '';
}

function hasName(person) {
  return Boolean(nameOf(person));
}
